import { styled, alpha, Tooltip } from '@mui/material';
import MuiIconButton, { IconButtonProps } from '@mui/material/IconButton';
import { Link } from 'react-router-dom';

const StyledIconButton = styled(MuiIconButton)<{ to?: string }>(({ theme }) => {
  const tone = (main: string) => ({
    color: main,
    borderColor: alpha(main, 0.4),
    '&:hover': {
      borderColor: main,
      backgroundColor: alpha(main, 0.1),
    },
  });

  return {
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    '&.MuiIconButton-colorPrimary': tone(theme.palette.primary.main),
    '&.MuiIconButton-colorSecondary': tone(theme.palette.secondary.main),
    '&.MuiIconButton-colorInfo': tone(theme.palette.info.main),
    '&.MuiIconButton-colorSuccess': tone(theme.palette.success.main),
    '&.MuiIconButton-colorWarning': tone(theme.palette.warning.main),
    '&.MuiIconButton-colorError': tone(theme.palette.error.main),
    '&.Mui-disabled': {
      opacity: 0.4,
      color: theme.palette.text.disabled,
      borderColor: theme.palette.divider,
      backgroundColor: 'transparent',
    },
  };
});

type ActionIconButtonProps = Omit<IconButtonProps, 'title'> & {
  title?: string;
  to?: string;
};

export const ActionIconButton = ({ title, to, 'aria-label': ariaLabel, ...rest }: ActionIconButtonProps) => {
  const accessibleName = ariaLabel ?? title;
  const button = to ? (
    <StyledIconButton component={Link} to={to} aria-label={accessibleName} {...rest} />
  ) : (
    <StyledIconButton aria-label={accessibleName} {...rest} />
  );
  if (!title) return button;

  return (
    <Tooltip title={title}>{rest.disabled ? <span style={{ display: 'inline-flex' }}>{button}</span> : button}</Tooltip>
  );
};

export default ActionIconButton;
